// src/components/ManagedInvoices.js
import React, { useState, useEffect } from 'react';
import api from '../api/api';
import moment from 'moment-timezone';
import '../styles/ManagedInvoices.css';
import { useNavigate } from 'react-router-dom';

const ManagedInvoices = () => {
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => moment().format('YYYY-MM'));
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    contractNumber: '',
    dueDate: moment().format('YYYY-MM-DD'),
    status: 'pending',
    recurrence: '',
    amount: '',
  });

  const formatCurrency = (value) => {
    if (!value) return '¢0.00';
    const num = parseFloat(value).toFixed(2);
    const parts = num.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `¢${formattedInteger}.${decimalPart}`;
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    navigate('/login'); // cambia a '/login' si tu login está en otra ruta
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/managed-invoices');
      setInvoices(res.data);
    } catch (error) {
      console.error('Error al obtener facturas administradas:', error);
      if (error?.response?.status === 401) return handleUnauthorized();
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (e) => setSelectedMonth(e.target.value);

  // Filtrar por mes
  const filtered = invoices.filter((inv) =>
    moment(inv.dueDate).format('YYYY-MM') === selectedMonth
  );
  const pendingInvoices = filtered.filter((inv) => inv.status === 'pending');
  const paidInvoices = filtered.filter((inv) => inv.status === 'paid');

  const handleSearch = () => {
    /* opcional: recargar/rescatar facturas filtradas */
  };

  const openModal = () => {
    setFormData({
      name: '',
      contractNumber: '',
      dueDate: moment().format('YYYY-MM-DD'),
      status: 'pending',
      recurrence: '',
      amount: '',
    });
    setShowModal(true);
  };
  const closeModal = () => setShowModal(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((fd) => ({ ...fd, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.contractNumber || !formData.dueDate || !formData.amount) {
      alert('Por favor completa los campos obligatorios.');
      return;
    }
    try {
      await api.post('/api/managed-invoices', formData);
      alert('Factura creada con éxito.');
      setShowModal(false);
      fetchInvoices();
    } catch (error) {
      console.error('Error al crear factura:', error);
      if (error?.response?.status === 401) return handleUnauthorized();
      alert('Error al crear factura');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/api/managed-invoices/${id}`, { status });
      fetchInvoices();
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      if (error?.response?.status === 401) return handleUnauthorized();
      alert('No se pudo actualizar el estado.');
    }
  };

  const handlePrint = () => window.print();

  // Totales
  const totalPending = pendingInvoices.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0);
  const totalPaid = paidInvoices.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0);

  if (loading) {
    return (
      <div className="mi-container">
        <p>Cargando facturas...</p>
      </div>
    );
  }

  return (
    <div className="mi-container">
      <h1>Administración de Facturas</h1>

      <div className="mi-search-bar no-print">
        <label>Seleccionar Mes:</label>
        <input type="month" value={selectedMonth} onChange={handleMonthChange} />
        <button className="mi-search-button" onClick={handleSearch}>
          Buscar
        </button>
        <button className="mi-print-button" onClick={handlePrint}>
          Imprimir
        </button>
      </div>

      <div className="mi-actions no-print">
        <button className="mi-add-button" onClick={openModal}>
          Añadir Factura
        </button>
      </div>

      <div className="printable-area">
        <h2>Facturas Pendientes</h2>
        {pendingInvoices.length === 0 ? (
          <p>No hay facturas pendientes para este mes.</p>
        ) : (
          <table className="mi-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>N° Contrato</th>
                <th>Fecha Límite</th>
                <th>Recurrencia</th>
                <th className="no-print">Estado</th>
                <th>Monto</th>
              </tr>
            </thead>
            <tbody>
              {pendingInvoices.map((inv) => (
                <tr key={inv.id}>
                  <td data-label="Nombre">{inv.name}</td>
                  <td data-label="Contrato">{inv.contractNumber}</td>
                  <td data-label="Fecha">{moment(inv.dueDate).format('DD/MM/YYYY')}</td>
                  <td data-label="Recurrencia">{inv.recurrence || '—'}</td>
                  <td className="no-print" data-label="Estado">
                    <select
                      className="mi-status-select"
                      value={inv.status}
                      onChange={(e) => handleStatusChange(inv.id, e.target.value)}
                    >
                      <option value="pending">Pendiente</option>
                      <option value="paid">Pagada</option>
                    </select>
                  </td>
                  <td data-label="Monto">{formatCurrency(inv.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h2>Facturas Pagadas</h2>
        {paidInvoices.length === 0 ? (
          <p>No hay facturas pagadas para este mes.</p>
        ) : (
          <table className="mi-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>N° Contrato</th>
                <th>Fecha Límite</th>
                <th>Recurrencia</th>
                <th className="no-print">Estado</th>
                <th>Monto</th>
              </tr>
            </thead>
            <tbody>
              {paidInvoices.map((inv) => (
                <tr key={inv.id}>
                  <td data-label="Nombre">{inv.name}</td>
                  <td data-label="Contrato">{inv.contractNumber}</td>
                  <td data-label="Fecha">{moment(inv.dueDate).format('DD/MM/YYYY')}</td>
                  <td data-label="Recurrencia">{inv.recurrence || '—'}</td>
                  <td className="no-print" data-label="Estado">
                    <select
                      className="mi-status-select"
                      value={inv.status}
                      onChange={(e) => handleStatusChange(inv.id, e.target.value)}
                    >
                      <option value="pending">Pendiente</option>
                      <option value="paid">Pagada</option>
                    </select>
                  </td>
                  <td data-label="Monto">{formatCurrency(inv.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mi-resumen">
        <h3>Resumen del Mes</h3>
        <p>
          <strong>Pendiente:</strong> {formatCurrency(totalPending)}
        </p>
        <p>
          <strong>Pagadas:</strong> {formatCurrency(totalPaid)}
        </p>
      </div>

      {showModal && (
        <div className="mi-modal-overlay">
          <div className="mi-modal">
            <h2>Añadir Factura</h2>
            <form onSubmit={handleSubmit} className="mi-form">
              <label>
                Nombre *:
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </label>
              <label>
                Nº Contrato *:
                <input
                  type="text"
                  name="contractNumber"
                  value={formData.contractNumber}
                  onChange={handleChange}
                  required
                />
              </label>
              <label>
                Fecha Límite *:
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  required
                />
              </label>
              <label>
                Recurrencia:
                <select name="recurrence" value={formData.recurrence} onChange={handleChange}>
                  <option value="">No aplica</option>
                  <option value="semanal">Semanal</option>
                  <option value="quincenal">Quincenal</option>
                  <option value="mensual">Mensual</option>
                </select>
              </label>
              <label>
                Estado:
                <select name="status" value={formData.status} onChange={handleChange}>
                  <option value="pending">Pendiente</option>
                  <option value="paid">Pagada</option>
                </select>
              </label>
              <label>
                Monto *:
                <div className="mi-amount-field">
                  <span className="mi-amount-currency">¢</span>
                  <input
                    type="number"
                    step="0.01"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                    className="mi-amount-input"
                    placeholder="0.00"
                  />
                </div>
              </label>
              <div className="mi-form-actions">
                <button type="submit">Guardar</button>
                <button type="button" onClick={closeModal}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagedInvoices;
